import React, { FC, PropsWithChildren, useCallback, useEffect, useState } from 'react'
import { LayoutChangeEvent, StyleProp, View, ViewStyle } from 'react-native'
import Animated, {
    Easing,
    useAnimatedStyle,
    useSharedValue,
    withTiming,
} from 'react-native-reanimated'

type CollapsibleProps = PropsWithChildren<{
    open: boolean
    disabled?: boolean
    stayExpandedWhenDisabled?: boolean
    duration?: number
    easing?: (t: number) => number
    containerStyle?: StyleProp<ViewStyle>
    contentStyle?: StyleProp<ViewStyle>
    animateOpacity?: boolean
    collapsedHeight?: number
    testID?: string
}>

export const Collapsible: FC<CollapsibleProps> = ({
    open,
    disabled = false,
    stayExpandedWhenDisabled = false,
    duration = 220,
    easing = Easing.out(Easing.cubic),
    collapsedHeight = 0,
    animateOpacity = true,
    containerStyle,
    contentStyle,
    children,
    testID,
}) => {
    const isOpen = open && (!disabled || stayExpandedWhenDisabled)
    const allowPointerEvents = open && !disabled

    const [measuredPx, setMeasuredPx] = useState(0)
    const height = useSharedValue(collapsedHeight)
    const opacity = useSharedValue(isOpen ? 1 : 0)

    const onLayout = useCallback(
        (e: LayoutChangeEvent) => {
            const h = Math.round(e.nativeEvent.layout.height)
            if (!h) return

            // Android can emit transient smaller layout heights while collapsing.
            // Keep the largest measured content height to avoid truncated re-open animations.
            setMeasuredPx((previous) => {
                const next = h > previous ? h : previous
                if (next !== previous && isOpen) {
                    height.value = withTiming(next, { duration, easing })
                }
                return next
            })
            if (isOpen && h === measuredPx) {
                height.value = withTiming(h, { duration, easing })
            }
        },
        [isOpen, duration, easing, measuredPx, height]
    )

    useEffect(() => {
        if (measuredPx > 0) {
            height.value = withTiming(isOpen ? measuredPx : collapsedHeight, {
                duration,
                easing,
            })
        } else {
            if (!isOpen) height.value = collapsedHeight
        }
        if (animateOpacity) {
            opacity.value = withTiming(isOpen ? 1 : 0, { duration: Math.min(duration, 200) })
        }
    }, [isOpen, duration, easing, collapsedHeight, measuredPx, animateOpacity, height, opacity])

    const animatedWrapper = useAnimatedStyle(() => {
        return {
            overflow: isOpen ? 'visible' : 'hidden',
            opacity: animateOpacity ? opacity.value : 1,
            height: measuredPx > 0 ? height.value : undefined,
        }
    }, [isOpen, measuredPx])

    return (
        <Animated.View
            style={[animatedWrapper, containerStyle]}
            pointerEvents={allowPointerEvents ? 'auto' : 'none'}
            testID={testID}
            accessibilityState={{ expanded: isOpen }}
        >
            <View collapsable={false} onLayout={onLayout} style={contentStyle}>
                {children}
            </View>
        </Animated.View>
    )
}

export default Collapsible
